import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "app-content" }

import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import AppSidebar from '@/components/AppSidebar.vue'
import MobileView from '@/views/MobileView.vue'
import PwaInstallBanner from '@/components/PwaInstallBanner.vue'
import ConvDrawer from '@/components/dashboard/ConvDrawer.vue'
import AgentChatDrawerCp from '@/components/dashboard/AgentChatDrawerCp.vue'
import DashHeader from '@/components/dashboard/DashHeader.vue'
import GlobalSearch from '@/components/GlobalSearch.vue'
import PhoneWidget from '@/components/PhoneWidget.vue'
import InternalChatBubble from '@/components/InternalChatBubble.vue'
import ShortcutsHelp from '@/components/ShortcutsHelp.vue'
import AutoLogoutWarningModal from '@/components/AutoLogoutWarningModal.vue'
import ForceLogoutWarningModal from '@/components/ForceLogoutWarningModal.vue'
import AssignmentAlertModal from '@/components/AssignmentAlertModal.vue'
import WaitingAlertBanner from '@/components/WaitingAlertBanner.vue'
import { startWaitingAlerts, stopWaitingAlerts } from '@/composables/useWaitingAlert'
import { syncWebPush } from '@/composables/useWebPush'
import BroadcastComposeModal from '@/components/BroadcastComposeModal.vue'
import AppUpdateBanner from '@/components/AppUpdateBanner.vue'
import ScreenMirrorModal from '@/components/ScreenMirrorModal.vue'
import ScreenMirrorIndicator from '@/components/ScreenMirrorIndicator.vue'
import ScreenMirrorMarks from '@/components/ScreenMirrorMarks.vue'
import { screenMirrorOpen, startScreenMirrorAgent, stopScreenMirrorAgent } from '@/composables/useScreenMirror'
import SessionAdminModal from '@/components/SessionAdminModal.vue'
import { sessionAdminOpen } from '@/composables/useSessionAdmin'
import { useMobile } from '@/composables/useMobile'
import { useLogo } from '@/composables/useLogo'
import { useAutoLogout, performLogout, continueSession } from '@/composables/useAutoLogout'
import { globalSearchOpen, openGlobalSearch, closeGlobalSearch, toggleGlobalSearch } from '@/composables/useGlobalSearch'
import { shortcutsHelpOpen, toggleShortcutsHelp, closeShortcutsHelp } from '@/composables/useShortcutsHelp'
import { currentOpenConvId, closeDrawer, requestFocusReply, requestOpenMyConvs, goNextConv, goPrevConv, requestOpenChat, requestViewMonthFilter } from '@/composables/useChatTrigger'
import { loadMyTeamIds } from '@/composables/useMyTeamIds'
import { startConversationRealtime, stopConversationRealtime, reconnectRealtime, realtimeStatus } from '@/composables/useConversationRealtime'
import { isNativeApp, onAppResume } from '@/utils/nativeApp'
import { startMobilePush } from '@/composables/useMobilePush'
import { setAppLockMinutes, startAppLock } from '@/composables/useBiometricLogin'
import { getPublicSettings } from '@/services/wootdashSystemSettings'
import AppLockOverlay from '@/components/AppLockOverlay.vue'
import { checkNativeAppUpdate } from '@/composables/useNativeAppUpdate'
import { startMyActiveConversations, stopMyActiveConversations } from '@/composables/useMyActiveConversations'
import { startRailConversations, stopRailConversations } from '@/composables/useRailConversations'
import { loadCurrentAgentRole, resetCurrentAgentRole } from '@/composables/useCurrentAgentRole'
import { startSessionHeartbeat, stopSessionHeartbeat } from '@/composables/useSessionTracking'
import { useAgentChatCp } from '@/composables/useAgentChatCp'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { toggleInternalChatPopupFromShortcut } from '@/composables/useInternalChatPopup'
import { usePermission } from '@/composables/usePermission'
import { useAdmin } from '@/composables/useAdmin'
import { openBroadcastCompose } from '@/composables/useBroadcastCompose'
import { useBroadcast } from '@/composables/useBroadcast'
import { pushAssignmentAlert, startAssignmentAlerts, stopAssignmentAlerts } from '@/composables/useAssignmentAlert'
import SnoozeEndedModal from '@/components/SnoozeEndedModal.vue'
import { startSnoozeAlerts, stopSnoozeAlerts } from '@/composables/useSnoozeAlerts'
import { parseYoutubeId } from '@/utils/youtube'
import type { PendingBroadcast } from '@/types/broadcast'
import {
  headerLoading, headerLastUpdate, headerCountdown,
  headerMyCount, headerMyOpenCount, headerMyTeamFilter, headerMyFluxosFilter,
  headerEmitExport, headerEmitTestNotif, headerEmitToggle, headerEmitToggleFluxos,
} from '@/composables/useHeaderState'


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const route  = useRoute()
const router = useRouter()
const store  = useStore()
const sidebarCollapsed  = ref(true)
const isLoginRoute      = computed(() => route.name === 'login')
const isFullscreenRoute = computed(() => route.name === 'sla-metro')
const authUser          = computed(() => store.getters.authUser)
const { isMobile } = useMobile()

const agentChatCp = useAgentChatCp()
const { mode: chatRailMode } = useChatRailMode()
const { can } = usePermission()
const { globalSettings } = useAdmin()

// Arma o timer de logout automático por inatividade (login e SLA Telão ficam
// isentos — ver useAutoLogout.ts). Chamado uma única vez aqui: App.vue é a
// raiz da SPA e cobre tanto o layout desktop quanto o MobileView.
useAutoLogout()

// Dados do Dashboard vêm do WootDash (useWootDashLiveData + SignalR). No login só falta
// o escopo de times (resolveRoleScope / permissão por time).
watch(authUser, (user) => {
  if (user) loadMyTeamIds()
}, { immediate: true })

// App do celular (ionic/): notificações pelo Firebase pro agente logado; tocar numa notificação abre a conversa.
watch(() => authUser.value?.wootdash_token as string | undefined, token => {
  if (token && isNativeApp()) {
    void startMobilePush(token, id => requestOpenChat(id))
    // Trava ao voltar do segundo plano: o tempo vem de Configurações Gerais › App de celular.
    void getPublicSettings(token).then(s => setAppLockMinutes(s?.appLockMinutes ?? 0))
    // Versão nova do APK publicada: avisa (uma vez por versão), depois das perguntas do login.
    window.setTimeout(() => void checkNativeAppUpdate(), 6000)
  }
}, { immediate: true })

// Motor WootDash (Dashboard real + ConvDrawer portado, Fase 3 correção): o
// ConvDrawer é global (montado logo abaixo) e precisa do socket SignalR ativo e de
// "minhas conversas" atualizado mesmo em páginas que não são o Dashboard — por isso
// arma aqui, igual ao motor antigo acima, não dentro de uma view que desmonta.
watch(authUser, (user) => {
  const token = user?.wootdash_token
  if (token) {
    startConversationRealtime(token)
    startScreenMirrorAgent()
    startMyActiveConversations(token, user?.wootdash_agent_id ?? '')
    if (user?.wootdash_agent_id) loadCurrentAgentRole(token, user.wootdash_agent_id)
    startRailConversations(token, user?.wootdash_agent_id ?? '')
    startAssignmentAlerts(token, user?.wootdash_agent_id ?? '')
    // Popup "Adiamento chegou ao limite" (AdiamentoVencido do hub).
    startSnoozeAlerts(user?.wootdash_agent_id ?? '')
    // Só chega pra quem está no grupo "painel" do hub (administradores) — o backend filtra.
    startWaitingAlerts()
    // Este navegador já estava com as notificações do Windows ligadas: reconecta pro agente que entrou.
    void syncWebPush(token)
  } else {
    stopAssignmentAlerts()
    stopSnoozeAlerts()
    stopWaitingAlerts()
    stopScreenMirrorAgent()
    stopConversationRealtime()
    stopMyActiveConversations()
    resetCurrentAgentRole()
    stopRailConversations()
  }
}, { immediate: true })

// Arma o rastreio de sessão (Admin → Sessões, migração v30) — registro +
// heartbeat periódico desta aba, mesmo padrão immediate/watch(authUser) dos
// pollers acima. Ver useSessionTracking.ts.
watch(authUser, (user) => {
  if (user) startSessionHeartbeat(user, store, router)
  else stopSessionHeartbeat()
}, { immediate: true })

// Avisos em massa (tecla K): pendentes no login + novos pelo SignalR "NovoBroadcast" — ver useBroadcast.ts.
const broadcast = useBroadcast()
watch(() => authUser.value?.wootdash_token, (token) => {
  if (token) broadcast.start(token)
  else broadcast.stop()
}, { immediate: true })

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

// body/link vêm de texto livre digitado por um agente (compositor) — escapa
// antes de injetar no `html` do Swal.fire pra não abrir XSS armazenado entre
// agentes. youtubeId não precisa de escape: já sai filtrado pelo regex de
// parseYoutubeId ([\w-]{11}), não carrega HTML/aspas.
function buildBroadcastHtml(b: PendingBroadcast): string {
  const parts = [
    `<div style="text-align:left;white-space:pre-wrap;font-size:14px;line-height:1.5;margin-bottom:12px;">${escapeHtml(b.body)}</div>`,
  ]
  if (b.imageUrl) {
    parts.push(`<img src="${escapeHtml(b.imageUrl)}" style="max-width:100%;max-height:220px;border-radius:8px;object-fit:contain;margin-bottom:10px;" />`)
  }
  const ytId = b.youtubeUrl ? parseYoutubeId(b.youtubeUrl) : null
  if (ytId) {
    parts.push(`<iframe src="https://www.youtube.com/embed/${ytId}" style="width:100%;aspect-ratio:16/9;border:0;border-radius:8px;margin-bottom:10px;" allowfullscreen></iframe>`)
  }
  if (b.linkUrl) {
    parts.push(`<a href="${escapeHtml(b.linkUrl)}" target="_blank" rel="noopener noreferrer" style="color:#BC0C24;font-weight:600;word-break:break-all;">${escapeHtml(b.linkUrl)}</a>`)
  }
  return parts.join('')
}

// Guarda por id: o mesmo aviso pode voltar a entrar na fila (recarga dos pendentes) enquanto o modal está aberto.
let showingBroadcastId: string | null = null
async function showBroadcast(b: PendingBroadcast | null) {
  if (!b || showingBroadcastId === b.id) return
  // Outro alerta do Swal na tela: espera ele sair (o Swal só mostra uma janela por vez).
  if (Swal.isVisible()) { setTimeout(() => showBroadcast(broadcast.currentBroadcast.value), 700); return }
  showingBroadcastId = b.id
  const result = await Swal.fire({
    title: b.kind === 'relatorio' ? 'Relatório de Resolução' : b.kind === 'ciclo' ? 'Ciclo de envio' : 'Aviso',
    html: buildBroadcastHtml(b),
    confirmButtonText: 'Confirmado',
    confirmButtonColor: '#BC0C24',
    allowOutsideClick: false,
    allowEscapeKey: false,
  })
  showingBroadcastId = null
  // Só confirma a leitura com o clique em "Confirmado". Se outro Swal tomou o lugar (toast, erro…), o aviso
  // fechou sem o agente ler: volta a aparecer assim que a outra janela sair.
  if (!result.isConfirmed) { setTimeout(() => showBroadcast(broadcast.currentBroadcast.value), 700); return }
  await broadcast.ack(b.id)
}
watch(() => broadcast.currentBroadcast.value, b => { showBroadcast(b) })

function onKeydown(e: KeyboardEvent) {
  // Ctrl+K / Cmd+K — sempre
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    toggleGlobalSearch()
    return
  }

  // Ctrl+! — só preview visual do popup "Novo atendimento atribuído"
  // (AssignmentAlertModal), não atribui nada de verdade. ConvId sintético
  // (negativo, timestamp) pra nunca colidir com uma conversa real e sempre
  // empilhar uma nova prévia mesmo repetindo o atalho.
  if ((e.ctrlKey || e.metaKey) && e.key === '!') {
    e.preventDefault()
    pushAssignmentAlert({ convId: -Date.now(), contactName: 'Cliente de Teste' })
    return
  }

  // Ctrl+@ — Espelho ao vivo: escolhe um agente e vê a tela do sistema dele (permissão agents.screen_view).
  // "@" é Shift+2 tanto no teclado ABNT2 quanto no americano, então e.key já chega como '@'.
  if ((e.ctrlKey || e.metaKey) && (e.key === '@' || (e.shiftKey && e.code === 'Digit2'))) {
    if (!can('agents.screen_view')) return
    e.preventDefault()
    screenMirrorOpen.value = !screenMirrorOpen.value
    return
  }

  // Ctrl+Shift+S — Sessões dos agentes: revoga sessões e desconecta agentes (permissão sessions.manage, perfil Sistema).
  // e.code em vez de e.key: com Shift o key vem 'S' e, com Caps Lock, 's'.
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && e.code === 'KeyS') {
    if (!can('sessions.manage')) return
    e.preventDefault()
    sessionAdminOpen.value = !sessionAdminOpen.value
    return
  }

  // Ctrl+Shift+C — popup de conversas (chat interno + diretório). Sem o Google Workspace configurado, abre um exemplo.
  // e.code pelo mesmo motivo do Ctrl+Shift+S. Vale até dentro de campo de texto (não colide com digitação).
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && e.code === 'KeyC') {
    e.preventDefault()
    toggleInternalChatPopupFromShortcut()
    return
  }

  // Esc — fecha o que estiver aberto
  if (e.key === 'Escape') {
    if (shortcutsHelpOpen.value)            { closeShortcutsHelp(); return }
    if (globalSearchOpen.value)             { closeGlobalSearch();  return }
    if (currentOpenConvId.value !== null)   { closeDrawer();        return }
    return
  }

  // Atalhos de uma tecla nunca disparam com modificador — senão Ctrl+C pra copiar
  // texto selecionado (e.key continua 'c' com Ctrl segurado) abriria "Minhas
  // Conversas" em vez de copiar.
  if (e.ctrlKey || e.metaKey || e.altKey) return

  // Atalhos de uma tecla: não disparar dentro de inputs/textareas
  const tag = (document.activeElement?.tagName ?? '').toUpperCase()
  const editing = tag === 'INPUT' || tag === 'TEXTAREA' ||
    (document.activeElement as HTMLElement | null)?.isContentEditable
  if (editing) return

  switch (e.key) {
    case '/':
      e.preventDefault()
      openGlobalSearch()
      break
    case '?':
      e.preventDefault()
      toggleShortcutsHelp()
      break
    case 'n': case 'N':
      e.preventDefault()
      goNextConv()
      break
    case 'p': case 'P':
      e.preventDefault()
      goPrevConv()
      break
    case 'r': case 'R':
      e.preventDefault()
      requestFocusReply()
      break
    case 'c': case 'C':
      e.preventDefault()
      requestOpenMyConvs()
      break
    case 'k': case 'K':
      if (!can('broadcast.send') || !globalSettings.value.broadcastEnabled) return
      e.preventDefault()
      openBroadcastCompose()
      break
    case 'm': case 'M':
      if (!can('foco.view') || route.name === 'foco') return
      e.preventDefault()
      router.push({ name: 'foco' })
      break
  }
}

const { loadLogo } = useLogo()

// Abre a conversa indicada por uma notificação clicada — seja via aba nova
// (query param ?conv=) ou aba já existente (mensagem do service worker),
// nunca navegando para o Chatwoot.
function onSWMessage(e: MessageEvent) {
  if (e.data?.type === 'notif-open-conv' && e.data.convId) {
    requestOpenChat(String(e.data.convId))
  } else if (e.data?.type === 'notif-open-agent-chat-cp' && e.data.senderId) {
    chatRailMode.value = 'agent'
    agentChatCp.panelOpen.value = true
    agentChatCp.openThread(authUser.value?.wootdash_token ?? '', String(e.data.senderId))
  } else if (e.data?.type === 'notif-continue-session') {
    continueSession(store)
  } else if (e.data?.type === 'notif-view-month-filter') {
    requestViewMonthFilter()
    if (route.name !== 'dashboard') router.push({ name: 'dashboard' })
  }
}

// Dentro do app do celular (ionic/): o aviso "instalar o app" é do PWA e some; ao voltar do segundo plano, reconecta o
// tempo real se o sistema derrubou a conexão nesse meio-tempo.
const inNativeApp = isNativeApp()
let stopResume: (() => void) | null = null
let stopAppLock: (() => void) | null = null

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  loadLogo()
  stopResume = onAppResume(() => { if (authUser.value && realtimeStatus.value !== 'connected') reconnectRealtime() })
  stopAppLock = startAppLock(() => !!authUser.value)

  const convParam = route.query.conv
  if (convParam) {
    requestOpenChat(String(convParam))
    const { conv, ...rest } = route.query
    router.replace({ query: rest })
  }

  // "Abrir no WootDash" das mensagens do app no Google Chat: ?chat-interno=<id do colega> abre o chat interno com ele.
  const peerParam = route.query['chat-interno']
  if (peerParam) {
    chatRailMode.value = 'agent'
    agentChatCp.panelOpen.value = true
    void agentChatCp.openThread(authUser.value?.wootdash_token ?? '', String(peerParam))
    const query = { ...route.query }
    delete query['chat-interno']
    router.replace({ query })
  }

  navigator.serviceWorker?.addEventListener('message', onSWMessage)
})
onUnmounted(() => {
  stopResume?.()
  stopAppLock?.()
  window.removeEventListener('keydown', onKeydown)
  navigator.serviceWorker?.removeEventListener('message', onSWMessage)
})

async function appLogout() {
  await performLogout(store, router)
}

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (isLoginRoute.value || isFullscreenRoute.value)
      ? (_openBlock(), _createBlock(_component_router_view, { key: 0 }))
      : (_unref(isMobile))
        ? (_openBlock(), _createBlock(MobileView, { key: 1 }))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            _createVNode(DashHeader, {
              loading: _unref(headerLoading),
              "last-update": _unref(headerLastUpdate),
              countdown: _unref(headerCountdown),
              user: authUser.value,
              "my-count": _unref(headerMyCount),
              "my-open-count": _unref(headerMyOpenCount),
              "my-team-filter": _unref(headerMyTeamFilter),
              "my-fluxos-filter": _unref(headerMyFluxosFilter),
              onExport: _unref(headerEmitExport),
              onLogout: appLogout,
              onTestNotif: _unref(headerEmitTestNotif),
              onToggleMyTeamFilter: _unref(headerEmitToggle),
              onToggleMyFluxosFilter: _unref(headerEmitToggleFluxos)
            }, null, 8, ["loading", "last-update", "countdown", "user", "my-count", "my-open-count", "my-team-filter", "my-fluxos-filter", "onExport", "onTestNotif", "onToggleMyTeamFilter", "onToggleMyFluxosFilter"]),
            _createElementVNode("div", {
              class: _normalizeClass(["app-layout", { 'sidebar-open': !sidebarCollapsed.value }])
            }, [
              _createVNode(AppSidebar, {
                collapsed: sidebarCollapsed.value,
                "onUpdate:collapsed": _cache[0] || (_cache[0] = ($event: any) => ((sidebarCollapsed).value = $event))
              }, null, 8, ["collapsed"]),
              _createElementVNode("div", _hoisted_1, [
                _createVNode(_component_router_view, null, {
                  default: _withCtx(({ Component, route }) => [
                    (_openBlock(), _createBlock(_resolveDynamicComponent(Component), {
                      key: route.meta.remount ? `${String(route.name)}:${String(route.params.id ?? '')}` : undefined
                    }))
                  ]),
                  _: 1
                })
              ]),
              _createVNode(ConvDrawer),
              _createVNode(AgentChatDrawerCp)
            ], 2),
            _createVNode(GlobalSearch),
            _createVNode(ShortcutsHelp),
            _createVNode(PhoneWidget, {
              "sidebar-open": !sidebarCollapsed.value
            }, null, 8, ["sidebar-open"]),
            _createVNode(InternalChatBubble, {
              "sidebar-open": !sidebarCollapsed.value
            }, null, 8, ["sidebar-open"])
          ], 64)),
    (_unref(isMobile) && !isFullscreenRoute.value && !_unref(inNativeApp))
      ? (_openBlock(), _createBlock(PwaInstallBanner, { key: 3 }))
      : _createCommentVNode("", true),
    _createVNode(AutoLogoutWarningModal),
    (_unref(inNativeApp))
      ? (_openBlock(), _createBlock(AppLockOverlay, { key: 4 }))
      : _createCommentVNode("", true),
    _createVNode(ForceLogoutWarningModal),
    _createVNode(AssignmentAlertModal),
    (!isLoginRoute.value)
      ? (_openBlock(), _createBlock(SnoozeEndedModal, { key: 5 }))
      : _createCommentVNode("", true),
    _createVNode(WaitingAlertBanner),
    _createVNode(BroadcastComposeModal),
    _createVNode(AppUpdateBanner),
    (!isLoginRoute.value && !isFullscreenRoute.value && !_unref(isMobile))
      ? (_openBlock(), _createBlock(ScreenMirrorModal, { key: 6 }))
      : _createCommentVNode("", true),
    (!isLoginRoute.value && !isFullscreenRoute.value && !_unref(isMobile))
      ? (_openBlock(), _createBlock(SessionAdminModal, { key: 7 }))
      : _createCommentVNode("", true),
    (!isLoginRoute.value)
      ? (_openBlock(), _createBlock(ScreenMirrorIndicator, { key: 8 }))
      : _createCommentVNode("", true),
    (!isLoginRoute.value)
      ? (_openBlock(), _createBlock(ScreenMirrorMarks, { key: 9 }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})